import { isString } from "lodash-es";
import { ErrorCode, NatsError } from "nats.ws";

import type { NatsCaller } from "@/backend/natsstubs/NatsCaller";
import { NatsAlmsyncCommonClient } from "@/backend/natsstubs/almsync/common/client";
import { NatsAlmsyncJiraClient } from "@/backend/natsstubs/almsync/jira/client";
import type { GetDevLinksResponse } from "@/backend/natsstubs/almsync/jira/models";
import { NatsConfidenceVoteClient } from "@/backend/natsstubs/confidence_vote/client";
import type {
  AddVoteRequest,
  ConfidenceVote1,
  ConfidenceVoteSession,
  CreateConfidenceVoteSessionRequest,
  Participant,
  UpdateConfidenceVoteSessionRequest,
} from "@/backend/natsstubs/confidence_vote/models";
import { NatsOnboardingClient } from "@/backend/natsstubs/onboarding/client";
import type { CreateSessionRequest } from "@/backend/natsstubs/onboarding/models";
import { NatsSessionClient } from "@/backend/natsstubs/session/client";
import { captureException } from "@/error/sentry";
import { backlogSyncTimeout, iterationSyncTimeout } from "@/model/Settings";
import type { ArtId, BoardType } from "@/model/baseTypes";
import type { Reaction } from "@/model/card";
import type { SessionAlmStatus } from "@/model/session";
import type { Shape } from "@/model/shape";
import type { TimerData } from "@/model/timer";
import type { EventFilter } from "@/store/activity";
import { useArtStore } from "@/store/art";
import { useClientStore } from "@/store/client";
import { isNoUser } from "@/store/user";
import { stickerToString } from "@/utils/sticker";

import { NatsStickyClient } from "../natsstubs/sticky/client";
import type { RemoveStickyRequest } from "../natsstubs/sticky/models";
import type {
  SearchStickiesQuery,
  Subscription,
  SubscriptionLevel,
} from "../read/BackendReadSession";
import type {
  NormalizedServerAlmItemTypeMap,
  ServerAlmInfo,
  ServerAlmItemType,
  ServerAlmItemTypeMap,
  ServerAlmSource,
  ServerAlmStickyTypeData,
  ServerAlmType,
  ServerArt,
  ServerBacklogSyncState,
  ServerBoard,
  ServerBoardEventsResponse,
  ServerBoardIteration,
  ServerCategory,
  ServerCompany,
  ServerCompanySettings,
  ServerEvent,
  ServerFlexBackground,
  ServerFlexBoard,
  ServerFlexType,
  ServerInfo,
  ServerIterationSyncState,
  ServerKanbanConfigMap,
  ServerLicense,
  ServerLink,
  ServerLinkType,
  ServerMirrorResponse,
  ServerObjectives,
  ServerSearchResult,
  ServerSession,
  ServerSettings,
  ServerStatusWithData,
  ServerStickiesUpdate,
  ServerSticky,
  ServerStickyChange,
  ServerStickyGroupBoardIds,
  ServerStickerType,
  ServerStickyType,
  ServerSyncStatus,
} from "../serverModel";
import { isNormalizedServerAlmItemTypeMap } from "../serverModel";
import { denormalizedServerAlmItemTypeMap } from "./BaseBackendSession.utils";
import type { RpcBackend } from "./RpcBackend";

export class BaseBackendSession {
  private almSources?: ServerAlmSource[];
  protected almType: ServerAlmType;
  protected almSyncId?: string;
  private almConnectionId?: string;
  protected subscriptions: Record<SubscriptionLevel, Subscription[]> = {
    room: [],
    session: [],
    board: [],
  };
  protected sessionId = "";
  private confidenceVoteClient: NatsConfidenceVoteClient;
  private stickyClient: NatsStickyClient;
  private natsAlmsyncJiraClient: NatsAlmsyncJiraClient;
  natsAlmClient: NatsAlmsyncCommonClient;
  natsSessionClient: NatsSessionClient;
  natsOnboardingClient: NatsOnboardingClient;

  constructor(
    protected company: string,
    protected rpcBackend: RpcBackend,
    natsCaller: NatsCaller,
  ) {
    this.confidenceVoteClient = new NatsConfidenceVoteClient(natsCaller);
    this.stickyClient = new NatsStickyClient(natsCaller);
    this.natsAlmsyncJiraClient = new NatsAlmsyncJiraClient(natsCaller);
    this.natsAlmClient = new NatsAlmsyncCommonClient(natsCaller);
    this.natsSessionClient = new NatsSessionClient(natsCaller);
    this.natsOnboardingClient = new NatsOnboardingClient(natsCaller);
  }

  async initSession(id: string): Promise<boolean> {
    this.sessionId = id;
    const info = await this.getAlmSessionInfo();
    this.almType = info.alm_connection_type;
    this.rpcBackend.init(this.sessionId, this.almType);
    this.almSyncId = info.sync_session_id;
    this.almConnectionId = info.alm_connection_id;
    this.almSources = [];
    if (!this.almType) {
      return false;
    }
    this.almSources = await this.getAlmSources();
    return true;
  }

  getAlmSource(
    boardType: ServerBoard["board_type"],
    { teamId, artId }: { teamId?: ServerBoard["user_id"]; artId: ServerBoard["art_id"] },
  ): ServerAlmSource | undefined {
    if (boardType === "team") {
      return this.almSources?.find((s) => s.type === "team" && s.user_id === teamId);
    }
    if (boardType === "backlog" && useArtStore().isMultiArt) {
      return this.almSources?.find((source) => source.type === "art" && source.art_id === artId);
    }
  }

  subscriptionCount() {
    return Object.values(this.subscriptions).reduce(
      (count, subscriptions) => count + subscriptions.length,
      0,
    );
  }

  getAlmType() {
    return this.almType;
  }

  getAlmConnectionId(): string | undefined {
    return this.almConnectionId;
  }

  async unsubscribeLevel(level: SubscriptionLevel, connectionOpen = true) {
    const promises = new Array<Promise<void>>();
    switch (level) {
      // @ts-expect-error fallthrough is intentional
      case "room":
        promises.push(...this.doUnsubscribeLevel("room", connectionOpen));
      // @ts-expect-error fallthrough is intentional
      case "session":
        promises.push(...this.doUnsubscribeLevel("session", connectionOpen));
      // fall through
      case "board":
        promises.push(...this.doUnsubscribeLevel("board", connectionOpen));
    }
    await Promise.all(promises);
  }

  doUnsubscribeLevel(level: SubscriptionLevel, connectionOpen: boolean): Array<Promise<void>> {
    const subscriptions = this.subscriptions[level];
    this.subscriptions[level] = [];
    if (!connectionOpen) {
      return [];
    }
    return subscriptions.map((s) => this.unsubscribe(s));
  }

  async unsubscribe(subscription: Subscription): Promise<void> {
    try {
      return await subscription.drain();
    } catch (error) {
      if (error instanceof NatsError) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        if (error.code === ErrorCode.Disconnect) {
          return;
        }
      }
      captureException(error, {
        subscription: { id: subscription.getID(), subject: subscription.getSubject() },
      });
    } finally {
      subscription.unsubscribe();
    }
  }

  getLicense(): Promise<ServerLicense> {
    return this.rpcBackend.userCall("license.get");
  }

  getSessions(): Promise<ServerSession[]> {
    return this.rpcBackend.roomCall("session.list", []);
  }

  getAlmSessionInfo(): Promise<ServerAlmInfo> {
    return this.rpcBackend.almCall("session.get", [this.sessionId]);
  }

  getAlmStatus(): Promise<SessionAlmStatus> {
    return this.rpcBackend.almToolCall("session.get_status", [this.almSyncId]);
  }

  async hasMappedAlmUser(id?: string): Promise<boolean> {
    return this.rpcBackend.almToolCall("alm_user.is_mapped", [this.almConnectionId, id]);
  }

  getAlmSources(): Promise<ServerAlmSource[]> {
    return this.rpcBackend.almToolCall(`project_source.list`, [this.almConnectionId]);
  }

  getSettings(): Promise<ServerSettings> {
    return this.rpcBackend.sessionCall("settings.get");
  }

  getCompanySettings(): Promise<ServerCompanySettings> {
    return this.rpcBackend.companyCall("settings.list");
  }

  getCompany(): Promise<ServerCompany> {
    return this.rpcBackend.companyCall("company.get");
  }

  getArts(sessionId?: string): Promise<ServerArt[]> {
    return this.rpcBackend.staticSessionCall<ServerArt[]>("art.list", [
      sessionId || this.sessionId,
    ]);
  }

  getBoardIterations(
    withStatusDistribution: boolean,
    boardId?: string,
  ): Promise<Record<string, ServerBoardIteration[]>> {
    return this.rpcBackend.roomCall("board_iteration.list", [
      this.sessionId,
      withStatusDistribution,
      boardId,
    ]);
  }

  getLinks(): Promise<ServerLink[]> {
    return this.rpcBackend.sessionCall("link.list");
  }

  getLinkTypes(): Promise<ServerLinkType[]> {
    return this.rpcBackend.sessionCall("link_type.list");
  }

  async getBoards(sessionId?: string) {
    return this.rpcBackend.roomCall<Array<ServerBoard | ServerFlexBoard>>("board.list", [
      sessionId || this.sessionId,
    ]);
  }

  addBoard(type: string, name: string): Promise<string> {
    return this.rpcBackend.flexBoardCall("flexboard.add", [this.sessionId], {
      board_type: "flex",
      flexboard_type: type,
      name,
    });
  }

  updateBoard(id: string, name: string): Promise<void> {
    return this.rpcBackend.flexBoardCall("flexboard.update", [id], { name });
  }

  deleteBoard(id: string): Promise<void> {
    return this.rpcBackend.flexBoardCall("flexboard.delete", [id]);
  }

  getScale(boardId: string): Promise<number> {
    return this.rpcBackend.boardCall<number>("sticky.get_scale", [boardId]);
  }

  getBoardStickies(
    boardId: string,
    since?: number,
    withStatusDistribution = false,
  ): Promise<ServerSticky[] | ServerStickiesUpdate> {
    return this.rpcBackend.boardCall("sticky.list", [boardId], {
      timestamp: since,
      with_status_distribution: withStatusDistribution,
    });
  }

  getStickies(
    stickyIds: string[],
    withLinks?: boolean,
    withStatusDistribution?: boolean,
  ): Promise<ServerSticky[]> {
    return this.rpcBackend.staticSessionCall("sticky.list", [stickyIds], {
      with_links: withLinks,
      with_status_distribution: withStatusDistribution,
    });
  }

  async getStickyTypes(): Promise<ServerStickyType[]> {
    const ts = await this.rpcBackend.sessionCall<ServerStickyType[]>("sticky_type.list");
    return ts.sort((a, b) => (a.index || 0) - (b.index || 0));
  }

  async getStickerTypes(): Promise<ServerStickerType[]> {
    const response = await this.natsSessionClient.getStickerTypes(this.sessionId);
    return response.stickerTypes.map((st) => ({
      icon: st.icon,
      color: st.color,
      label: st.label,
    }));
  }

  async getAlmItemType(
    almType: string,
    boardType: BoardType,
    boardKeys: string[],
  ): Promise<ServerAlmItemTypeMap> {
    const response: NormalizedServerAlmItemTypeMap | ServerAlmItemTypeMap =
      await this.rpcBackend.almToolCall("item.get_by_alm_type", [
        this.almSyncId,
        almType,
        boardType,
        boardKeys,
      ]);

    return isNormalizedServerAlmItemTypeMap(response)
      ? denormalizedServerAlmItemTypeMap(response)
      : response;
  }

  getAlmItemTypeForSticky(stickyAlmId: string): Promise<ServerAlmItemType> {
    return this.rpcBackend.almToolCall("item.get_by_sticky", [this.almConnectionId, stickyAlmId]);
  }

  getObjectives(boardId: string, enriched: boolean): Promise<ServerObjectives> {
    return this.rpcBackend.boardCall("objective.list", [boardId, enriched]);
  }

  newSticky(
    boardId: ServerSticky["board_id"],
    props: Partial<ServerSticky>,
  ): Promise<ServerSticky["id"]> {
    return this.rpcBackend.boardCall("sticky.create", [boardId, null, props], {
      client_id: useClientStore().clientId,
      alm_type: this.almType,
    });
  }

  mirror(
    cardId: string,
    toBoardId: string,
    props?: Partial<ServerSticky>,
    aCenter?: ServerSticky["a_center"],
  ) {
    return this.rpcBackend.boardCall<ServerMirrorResponse>("sticky.mirror", [
      cardId,
      toBoardId,
      props,
      aCenter,
    ]);
  }

  move(cardId: string, teamId: string) {
    return this.rpcBackend.stickyCall("sticky.move", [cardId, +teamId]);
  }

  toRisk(cardId: string, boardId: string, text: string, typeId: string, teamId: string) {
    return this.rpcBackend.boardCall("sticky.create_on_risk_board", [
      boardId,
      { id: cardId, text, type_id: typeId, team_id: +teamId },
    ]);
  }

  addLink(cardId: string, toId: string): Promise<ServerLink> {
    return this.rpcBackend.sessionCall("link.create", [cardId, toId]);
  }

  addObjectiveLink(cardId: string, boardId: string, objectiveId: string): Promise<void> {
    return this.rpcBackend.staticSessionCall<void>("objective_link.create", [
      cardId,
      boardId,
      objectiveId,
    ]);
  }

  deleteLink(id: string): Promise<void> {
    return this.rpcBackend.staticSessionCall("link.delete", [id]);
  }

  deleteObjectiveLink(cardId: string, boardId: string, objectiveId: string): Promise<void> {
    return this.rpcBackend.staticSessionCall<void>("objective_link.delete", [
      cardId,
      boardId,
      objectiveId,
    ]);
  }

  syncIteration(teamId: string, iterationId: number): Promise<ServerIterationSyncState> {
    return this.rpcBackend.almToolCall(
      "session.sync_iteration",
      [this.almSyncId, teamId, iterationId],
      iterationSyncTimeout,
    );
  }

  async syncBacklog(artId?: ArtId): Promise<ServerBacklogSyncState> {
    const response = await this.rpcBackend.almToolCall(
      "session.sync_program_backlog",
      [this.almSyncId, artId],
      backlogSyncTimeout,
    );
    if (isString(response)) {
      return { success: true, status: response as ServerSyncStatus };
    }
    return response as ServerBacklogSyncState;
  }

  getFlexBackgrounds(): Promise<ServerFlexBackground[]> {
    return this.rpcBackend.flexBoardCall("background.list", []);
  }

  getFlexTypes(sessionId?: string): Promise<ServerFlexType[]> {
    return this.rpcBackend.flexBoardCall("board_type.list", [sessionId || this.sessionId]);
  }

  getCategories(): Promise<ServerCategory[]> {
    return this.rpcBackend.flexBoardCall("category.list", [this.sessionId]);
  }

  addCategory(name: string): Promise<string> {
    return this.rpcBackend.flexBoardCall("category.add", [this.sessionId, name]);
  }

  deleteCategory(id: string): Promise<void> {
    return this.rpcBackend.flexBoardCall("category.delete", [id]);
  }

  updateCategory(id: string, update: { name?: string; position?: number }): Promise<void> {
    return this.rpcBackend.flexBoardCall("category.update", [id], update);
  }

  addBoardToCategory(boardId: string, categoryId: string): Promise<void> {
    return this.rpcBackend.flexBoardCall("flexboard.add_to_category", [boardId, categoryId]);
  }

  removeBoardFromCategory(boardId: string, categoryId: string): Promise<void> {
    return this.rpcBackend.flexBoardCall("flexboard.remove_from_category", [boardId, categoryId]);
  }

  searchStickies(query: SearchStickiesQuery): Promise<ServerSearchResult[]> {
    return this.rpcBackend.sessionCall("sticky.search", [
      {
        text: query.text,
        team_id: query.teams?.map(({ id }) => (id ? +id : null)),
        type_id: query.types?.map(({ id }) => id),
        statuses: query.statuses?.map(({ name }) => name),
        flag_type: query.flags?.map(stickerToString),
        board_id: query.boards?.map(({ id }) => id),
        assignee_id: query.assignees?.map((u) => (isNoUser(u) ? null : u.id)),
        reporter_id: query.reporters?.map((u) => (isNoUser(u) ? null : u.id)),
      },
    ]);
  }

  getEvents(): Promise<ServerEvent[]> {
    return this.rpcBackend.sessionCall("timer.list");
  }

  createTimerEvent(
    data: Partial<TimerData>,
    target: { boardId?: string; artId?: string },
  ): Promise<string> {
    return this.rpcBackend.sessionCall("timer.create", [data], {
      board_id: target.boardId,
      art_id: target.artId,
    });
  }

  async createConfidenceVoteSession(
    payload: CreateConfidenceVoteSessionRequest,
  ): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.createConfidenceVoteSession(payload);
    return res.session;
  }

  async getConfidenceVoteSessions(piSessionId?: string): Promise<ConfidenceVoteSession[]> {
    const res = await this.confidenceVoteClient.getConfidenceVoteSessions({ piSessionId });
    return res.sessions;
  }

  async endConfidenceVoteSession(votingSessionId: string): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.endConfidenceVoteSession(votingSessionId);
    return res.session;
  }

  async joinConfidenceVoteSession(votingSessionId: string): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.joinConfidenceVoteSession(votingSessionId);
    return res.session;
  }

  async getConfidenceVoteSessionParticipants(votingSessionId: string): Promise<Participant[]> {
    const res =
      await this.confidenceVoteClient.getConfidenceVoteSessionParticipants(votingSessionId);
    return res.participants;
  }

  async getMyVote(votingSessionId: string): Promise<ConfidenceVote1 | null> {
    const res = await this.confidenceVoteClient.getMyVote(votingSessionId);
    return res.vote ?? null;
  }

  async addVote(votingSessionId: string, payload: AddVoteRequest): Promise<void> {
    await this.confidenceVoteClient.addVote(payload, votingSessionId);
  }

  async deleteConfidenceVoteSession(votingSessionId: string): Promise<void> {
    await this.confidenceVoteClient.deleteConfidenceVoteSession(votingSessionId);
  }

  async updateConfidenceVoteSession(
    votingSessionId: string,
    payload: UpdateConfidenceVoteSessionRequest,
  ): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.updateConfidenceVoteSession(
      payload,
      votingSessionId,
    );
    return res.session;
  }

  async startConfidenceVoteSession(votingSessionId: string): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.startConfidenceVoteSession(votingSessionId);
    return res.session;
  }

  async deleteCard(cardId: string, payload: RemoveStickyRequest): Promise<void> {
    await this.stickyClient.removeSticky(payload, cardId);
  }

  updateTimerEvent(id: string, data: Partial<TimerData>): Promise<void> {
    return this.rpcBackend.sessionCall("timer.update", [id, data]);
  }

  deleteEvent(id: string): Promise<void> {
    return this.rpcBackend.sessionCall("timer.delete", [id]);
  }

  getServerInfo(useHttp: boolean, timeout?: number): Promise<ServerInfo> {
    return this.rpcBackend.serverCall(
      "info.get",
      [],
      useHttp ? { useBackend: 1 } : undefined,
      timeout,
    );
  }

  getStatusWithData(sizeMB: number, useHttp?: boolean): Promise<ServerStatusWithData> {
    if (sizeMB < 0 || (useHttp && sizeMB > 2)) {
      throw Error("Size must be between 0-2 MB for HTTP request");
    } else if (!useHttp && sizeMB > 0.5) {
      throw Error("Size must be between 0-0.5 MB for WS request");
    }
    return this.rpcBackend.statusCall(
      "status.get_random_data",
      [sizeMB],
      useHttp ? { useBackend: 1 } : undefined,
    );
  }

  addReaction(cardId: string, reaction: Reaction): Promise<void> {
    return this.rpcBackend.boardCall("reaction.add", [cardId, reaction]);
  }

  removeReaction(cardId: string, reaction: Reaction): Promise<void> {
    return this.rpcBackend.boardCall("reaction.remove", [cardId, reaction]);
  }

  addShape(boardId: string, shape: Shape): Promise<string> {
    return this.rpcBackend.boardCall("shape.add", [boardId, shape]);
  }

  editShape(boardId: string, shape: Shape): Promise<void> {
    return this.rpcBackend.boardCall("shape.edit", [boardId, shape]);
  }

  removeShape(boardId: string, shapeId: string): Promise<void> {
    return this.rpcBackend.boardCall("shape.remove", [boardId, shapeId]);
  }

  async getDevLinks(issueKey: string): Promise<GetDevLinksResponse["devLinks"]> {
    if (!this.almSyncId) return [];
    const response = await this.natsAlmsyncJiraClient.getDevLinks({ issueKey }, this.almSyncId);
    return response.devLinks;
  }

  async getAlmStickyTypes(): Promise<ServerAlmStickyTypeData[]> {
    return this.almType ? this.rpcBackend.almToolCall("alm_type.list", [this.almSyncId]) : [];
  }

  async getCachedKanbanConfigs(teamIds: string[]): Promise<ServerKanbanConfigMap> {
    return await this.getKanbanConfigs(teamIds, false);
  }

  async getAlmKanbanConfigs(teamIds: string[]): Promise<ServerKanbanConfigMap> {
    return await this.getKanbanConfigs(teamIds, true);
  }

  async getKanbanConfigs(teamIds: string[], force: boolean): Promise<ServerKanbanConfigMap> {
    return this.almType
      ? this.rpcBackend.almToolCall("kanban_config.list", [
          this.almConnectionId,
          teamIds.map((id) => +id),
          force,
        ])
      : Object.fromEntries(teamIds.map((id) => [id, []]));
  }

  getBoardIdsOfGroupedStickies(stickyId: string) {
    return this.rpcBackend.staticSessionCall<ServerStickyGroupBoardIds>(
      "sticky.get_group_board_ids",
      [stickyId],
    );
  }

  getStickyChanges(id: string) {
    return this.rpcBackend.staticSessionCall<ServerStickyChange[]>("sticky.changes", [id]);
  }

  duplicateFlex(boardId: string, sessionId: string) {
    return this.rpcBackend.flexBoardCall<void>("flexboard.duplicate", [boardId, sessionId]);
  }

  /**
   * Returns the sum of all changes made to the board's cards between the two dates.
   */
  getBoardDiff(id: string, from: Date, to: Date, filters?: EventFilter[], changeTypes?: string[]) {
    return (
      this.rpcBackend
        .boardCall<ServerStickyChange[] | ServerBoardEventsResponse>("board.diff", [], {
          board_id: id,
          from_time: from.getTime(),
          to_time: to.getTime(),
          filters,
          change_types: changeTypes,
        })
        // Handle both the old and new response format (TODO: Remove after backend is updated)
        .then((response) => (Array.isArray(response) ? response : response.results))
    );
  }

  /**
   * Returns the list of all changes made to the board's cards between the two dates.
   */
  getBoardEventLog(
    id: string,
    from: Date,
    to: Date,
    filters?: EventFilter[],
    changeTypes?: string[],
  ) {
    return this.rpcBackend.boardCall<ServerBoardEventsResponse>("board.event_log", [], {
      board_id: id,
      from_time: from.getTime(),
      to_time: to.getTime(),
      filters,
      change_types: changeTypes,
    });
  }

  async getStatusDistribution(params: {
    boardId?: string;
    teamId?: string;
    iterationId?: number;
    artId?: number;
    stickyId?: string;
    objectiveId?: string;
  }) {
    const response = await this.natsSessionClient.getStatusDistribution(
      {
        boardId: params.boardId,
        teamId: params.teamId,
        iterationId: params.iterationId,
        artId: params.artId,
        stickyId: params.stickyId,
        objectiveId: params.objectiveId,
      },
      this.sessionId,
    );

    return response.items;
  }

  async getCapacityAllocation(teamIds: number[]) {
    const response = await this.natsSessionClient.getCapacityAllocation(
      { teamIds },
      this.sessionId,
    );

    return { data: response.items, warnings: response.warnings ?? [] };
  }

  async getDependencyFlow(teamIds: number[]) {
    const response = await this.natsSessionClient.getDependencyFlow({ teamIds }, this.sessionId);

    return { data: response.items, warnings: response.warnings ?? [] };
  }

  async getBurndownChartData(teamIds: number[], iterationId: number | null) {
    const response = await this.natsSessionClient.getBurndown(
      { teamIds, iterationId },
      this.sessionId,
    );
    return response.dataPoints;
  }

  // NATS stub requires a non-empty tuple; callers already guarantee at least one team ID
  async getPredictability(teamIds: [number, ...number[]], includeArchived: boolean) {
    return await this.natsSessionClient.getPredictability(
      { teamIds, includeArchived },
      this.sessionId,
    );
  }

  async listOnboardingPresets() {
    const response = await this.natsOnboardingClient.listPresets();
    return response.presets;
  }

  async createOnboardingSession(params: CreateSessionRequest) {
    return await this.natsOnboardingClient.createSession(params);
  }
}
